export type Extractor<Item, T> = (item: Item) => T | undefined | null
export type Comparator<Item, T> = (
  extractor: Extractor<Item, T>,
  reverse?: boolean,
) => (invertOrdering?: boolean) => (a: Item, b: Item) => number

const normalizeNumberForSorting = (n?: number | null) =>
  Number.isNaN(n) ? Number.NEGATIVE_INFINITY : n ?? Number.MIN_SAFE_INTEGER

export const getNumberComparator =
  <Item>(extractor: Extractor<Item, number>, reverse = false) =>
  (invertOrdering = false) =>
  (itemA: Item, itemB: Item) => {
    const compareValA = normalizeNumberForSorting(extractor(itemA))
    const compareValB = normalizeNumberForSorting(extractor(itemB))
    return compareValA === compareValB
      ? 0
      : (compareValA - compareValB) * (invertOrdering !== reverse ? -1 : 1)
  }

const maybeBooleanToNumber = (value: boolean | null | undefined) =>
  value ? 1 : value === false ? 0 : -1

export const getBooleanComparator =
  <Item>(extractor: Extractor<Item, boolean>, reverse = false) =>
  (invertOrdering = false) =>
  (itemA: Item, itemB: Item) =>
    (maybeBooleanToNumber(extractor(itemA)) -
      maybeBooleanToNumber(extractor(itemB))) *
    (invertOrdering !== reverse ? -1 : 1)

const maybeDateToSortNumber = (value: Date | null | undefined) =>
  normalizeNumberForSorting(value?.getTime())

export const getDateComparator =
  <Item>(extractor: Extractor<Item, Date>, reverse = false) =>
  (invertOrdering = false) =>
  (itemA: Item, itemB: Item) =>
    (maybeDateToSortNumber(extractor(itemA)) -
      maybeDateToSortNumber(extractor(itemB))) *
    (invertOrdering !== reverse ? -1 : 1)

const normalizeStringForSorting = (s?: string | null) =>
  typeof s !== 'string' ? '' : ` ${s}`

export const getStringComparator =
  <Item>(extractor: Extractor<Item, string>, reverse = false) =>
  (invertOrdering = false) =>
  (itemA: Item, itemB: Item) =>
    normalizeStringForSorting(extractor(itemB)).localeCompare(
      normalizeStringForSorting(extractor(itemA)),
    ) * (invertOrdering !== reverse ? -1 : 1)

export const getUndefinedComparator = () => () => () => 0
